import { useEffect, useState } from 'react';
import { usePortfolio } from '../context/PortfolioContext';

function SummaryCards() {
  const { summary } = usePortfolio();

  // Track mobile breakpoint so we can compact the cards' content on phones only.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const formatCurrency = (value, decimals = 0) => {
    if (value === undefined || value === null) return '-';
    const num = Number(value);
    if (isNaN(num)) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  // Compact (£20K, £1.5K) for mobile only.
  const formatCurrencyCompact = (value) => {
    if (value === undefined || value === null) return '-';
    const num = Number(value);
    if (isNaN(num)) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(num);
  };

  // Pick the right currency formatter based on viewport.
  const fmt = (value) => (isMobile ? formatCurrencyCompact(value) : formatCurrency(value, 0));

  const formatPercent = (value) => {
    if (value === undefined || value === null) return '-';
    const num = Number(value);
    if (isNaN(num)) return '-';
    return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
  };

  const totalValueRaw = summary?.total_market_value_gbp || summary?.total_value_gbp;

  const allCards = [
    {
      label: isMobile ? 'Value' : 'Total Value',
      value: fmt(totalValueRaw),
      fullValue: formatCurrency(totalValueRaw, 0),
      sub: isMobile ? 'Market' : 'Market Value',
      color: 'cyan',
      hideOnMobile: true, // already shown in the page header on mobile
    },
    {
      label: isMobile ? 'P&L' : 'Total P&L',
      value: fmt(summary?.total_pnl_gbp),
      fullValue: formatCurrency(summary?.total_pnl_gbp, 0),
      sub: isMobile ? 'Realized + Unr.' : 'Realized + Unrealized',
      color: summary?.total_pnl_gbp >= 0 ? 'green' : 'red',
      isPositive: summary?.total_pnl_gbp >= 0,
    },
    {
      label: 'Return',
      value: formatPercent(summary?.return_pct),
      sub: isMobile ? 'On capital' : 'On invested capital',
      color: summary?.return_pct >= 0 ? 'gold' : 'red',
      isPositive: summary?.return_pct >= 0,
    },
    {
      label: isMobile ? 'Invested' : 'Invested Capital',
      value: fmt(summary?.net_capital_gbp),
      fullValue: formatCurrency(summary?.net_capital_gbp, 0),
      sub: isMobile ? 'Net flow' : 'Deposits - Withdrawals',
      color: 'blue',
    },
    {
      label: 'Dividends',
      value: fmt(summary?.total_dividends_gbp),
      fullValue: formatCurrency(summary?.total_dividends_gbp, 0),
      sub: isMobile ? 'Income' : 'Total income',
      color: 'purple',
    },
    {
      label: isMobile ? 'Unrealized' : 'Unrealized P&L',
      value: fmt(summary?.total_unrealized_gbp),
      fullValue: formatCurrency(summary?.total_unrealized_gbp, 0),
      sub: isMobile ? 'Open' : 'Open positions',
      color: summary?.total_unrealized_gbp >= 0 ? 'green' : 'red',
      isPositive: summary?.total_unrealized_gbp >= 0,
    },
    {
      label: isMobile ? 'Cash' : 'Cash Available',
      value: fmt(summary?.cash_available_gbp),
      fullValue: formatCurrency(summary?.cash_available_gbp, 0),
      sub: 'Available',
      color: 'blue',
    },
  ];

  const cards = allCards.filter((c) => !(isMobile && c.hideOnMobile));

  return (
    <div
      className={`dashboard-grid dashboard-grid-4${isMobile ? ' is-3up-mobile' : ''}`}
      style={{ marginBottom: '1.5rem' }}
    >
      {cards.map((card, idx) => (
        <div key={idx} className={`summary-card ${card.color}`}>
          <div className="summary-card-label">{card.label}</div>
          <div 
            className="summary-card-value"
            title={card.fullValue}
            style={{ color: card.isPositive === false ? 'var(--red)' : card.isPositive === true ? 'var(--green)' : undefined }}
          >
            {card.value}
          </div>
          <div className="summary-card-sub">{card.sub}</div>
        </div>
      ))}
    </div>
  );
}

export default SummaryCards;
